import { useEffect } from 'react'
import { Loader2 } from 'lucide-react'
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom'
import { AuthenticatedLayout } from '@/components/layout/authenticated-layout'
import { useInstallations } from '@/features/installations/hooks/useInstallations'
import { NoInstallationsHome } from '@/features/installations/pages/no-installations-home'
import { InstallationCreatePage } from '@/features/installations/pages/installation-create-page'
import { DashboardHome } from '@/features/installations/pages/dashboard-home'
import {
  InstallationConfigurationIndexRedirect,
  InstallationConfigurationRoutes,
} from '@/features/installations/pages/installation-configuration-routes'
import { InstallationFuelsPage } from '@/features/installations/pages/installation-fuels-page'
import { InstallationOtherMaterialCategoriesPage } from '@/features/installations/pages/installation-other-material-categories-page'
import { InstallationPrecursorCnCodesPage } from '@/features/installations/pages/installation-precursor-cn-codes-page'
import { InstallationPrecursorCnCodeSourceGroupsPage } from '@/features/installations/pages/installation-precursor-cn-code-source-groups-page'
import { InstallationProducedCnCodesPage } from '@/features/installations/pages/installation-produced-cn-codes-page'
import { InstallationScrapCategoriesPage } from '@/features/installations/pages/installation-scrap-categories-page'
import { SupplierInstallationEditPage } from '@/features/suppliers/pages/supplier-installation-edit-page'
import { SupplierOrganizationAddressPage } from '@/features/suppliers/pages/supplier-organization-address-page'
import { SupplierOrganizationContactPage } from '@/features/suppliers/pages/supplier-organization-contact-page'
import { SupplierOrganizationHomePage } from '@/features/suppliers/pages/supplier-organization-home-page'
import { SupplierOrganizationInstallationsPage } from '@/features/suppliers/pages/supplier-organization-installations-page'
import { SupplierOrganizationLayout } from '@/features/suppliers/pages/supplier-organization-layout'
import { SuppliersPage } from '@/features/suppliers/pages/suppliers-page'
import {
  pickDefaultInstallationId,
  readLastInstallationId,
  writeLastInstallationId
} from '@/features/installations/lib/last-installation-id'
import { LanguageSettingsPage } from '@/features/settings/pages/language-settings-page'
import { ThemeSettingsPage } from '@/features/settings/pages/theme-settings-page'
import { AccountPreferencesLayout } from '@/features/settings/account-preferences-layout'
import { SettingsAccessGate } from '@/features/settings/settings-access-gate'
import { OrganizationCompanyPage } from '@/features/organization-settings/pages/organization-company-page'
import { OrganizationActivityPage } from '@/features/organization-users/pages/organization-activity-page'
import { OrganizationUsersPage } from '@/features/organization-users/pages/organization-users-page'
import { UsersAccessGate } from '@/features/organization-users/users-access-gate'

function LoadingScreen() {
  return (
    <div className="flex min-h-svh items-center justify-center">
      <Loader2 className="size-8 animate-spin text-muted-foreground" />
    </div>
  )
}

function RedirectToDashboard() {
  const navigate = useNavigate()
  const { data: installations, isSuccess } = useInstallations()

  useEffect(() => {
    if (!isSuccess || !installations?.length) {
      return
    }
    const targetId = pickDefaultInstallationId(installations, readLastInstallationId())
    if (targetId) {
      navigate(`/dashboard/${targetId}`, { replace: true })
    }
  }, [isSuccess, installations, navigate])

  return <LoadingScreen />
}

function OperatorsInstallationShell() {
  const { installationId } = useParams<{ installationId: string }>()
  const navigate = useNavigate()
  const { data: installations, isSuccess } = useInstallations()

  const fallbackId =
    isSuccess && installations?.length
      ? pickDefaultInstallationId(installations, readLastInstallationId())
      : undefined

  useEffect(() => {
    if (!isSuccess || !installations?.length || !installationId) {
      return
    }
    const isValid = installations.some(item => item.id === installationId)
    if (!isValid && fallbackId) {
      navigate(`/dashboard/${fallbackId}`, { replace: true })
    }
  }, [isSuccess, installations, installationId, fallbackId, navigate])

  useEffect(() => {
    if (!installationId || !installations?.length) {
      return
    }
    const isValid = installations.some(item => item.id === installationId)
    if (isValid) {
      writeLastInstallationId(installationId)
    }
  }, [installationId, installations])

  if (!isSuccess || !installations?.length || !installationId) {
    return <LoadingScreen />
  }

  const isValid = installations.some(item => item.id === installationId)
  if (!isValid) {
    return <LoadingScreen />
  }

  return <AuthenticatedLayout />
}

function LegacySettingsOrganizationRedirect() {
  const { installationId } = useParams<{ installationId: string }>()
  if (!installationId) {
    return null
  }
  return <Navigate to={`/dashboard/${installationId}/organization/company`} replace />
}

function LegacySettingsLanguageRedirect() {
  const { installationId } = useParams<{ installationId: string }>()
  if (!installationId) {
    return null
  }
  return <Navigate to={`/dashboard/${installationId}/account/language`} replace />
}

function LegacySettingsThemeRedirect() {
  const { installationId } = useParams<{ installationId: string }>()
  if (!installationId) {
    return null
  }
  return <Navigate to={`/dashboard/${installationId}/account/theme`} replace />
}

function LegacySettingsIndexRedirect() {
  const { installationId } = useParams<{ installationId: string }>()
  if (!installationId) {
    return null
  }
  return <Navigate to={`/dashboard/${installationId}/organization/company`} replace />
}

function SupplierOrganizationEditRedirect() {
  const { installationId, supplierOrganizationId } = useParams<{
    installationId: string
    supplierOrganizationId: string
  }>()
  if (!installationId || !supplierOrganizationId) {
    return null
  }
  return (
    <Navigate
      to={`/dashboard/${installationId}/suppliers/${supplierOrganizationId}/home`}
      replace
    />
  )
}

export default function AuthenticatedApp() {
  const { data: installations = [], isLoading, isFetched } = useInstallations()

  if (isLoading || !isFetched) {
    return <LoadingScreen />
  }

  if (installations.length === 0) {
    return (
      <Routes>
        <Route path="/" element={<NoInstallationsHome />} />
        <Route path="/installations/new" element={<InstallationCreatePage />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/" element={<RedirectToDashboard />} />
      <Route path="/dashboard" element={<RedirectToDashboard />} />
      <Route path="/installations/new" element={<InstallationCreatePage />} />
      <Route path="/dashboard/:installationId" element={<OperatorsInstallationShell />}>
        <Route index element={<DashboardHome />} />
        <Route path="suppliers" element={<SuppliersPage />} />
        <Route
          path="suppliers/:supplierOrganizationId/installations/:supplierInstallationId/edit"
          element={<SupplierInstallationEditPage />}
        />
        <Route
          path="suppliers/:supplierOrganizationId/edit"
          element={<SupplierOrganizationEditRedirect />}
        />
        <Route path="suppliers/:supplierOrganizationId" element={<SupplierOrganizationLayout />}>
          <Route index element={<Navigate to="home" replace />} />
          <Route path="home" element={<SupplierOrganizationHomePage />} />
          <Route path="address" element={<SupplierOrganizationAddressPage />} />
          <Route path="contact" element={<SupplierOrganizationContactPage />} />
          <Route path="installations" element={<SupplierOrganizationInstallationsPage />} />
        </Route>
        <Route path="installation/configuration" element={<InstallationConfigurationRoutes />}>
          <Route index element={<InstallationConfigurationIndexRedirect />} />
          <Route
            path="precursor-cn-codes"
            element={<InstallationPrecursorCnCodesPage />}
          />
          <Route
            path="precursor-cn-codes/:precursorCnCodeId/source-groups"
            element={<InstallationPrecursorCnCodeSourceGroupsPage />}
          />
          <Route
            path="produced-cn-codes"
            element={<InstallationProducedCnCodesPage />}
          />
          <Route path="fuels" element={<InstallationFuelsPage />} />
          <Route path="scrap-categories" element={<InstallationScrapCategoriesPage />} />
          <Route
            path="other-material-categories"
            element={<InstallationOtherMaterialCategoriesPage />}
          />
        </Route>
        <Route
          path="users"
          element={
            <UsersAccessGate>
              <OrganizationUsersPage />
            </UsersAccessGate>
          }
        />
        <Route
          path="organization/activity"
          element={
            <UsersAccessGate>
              <OrganizationActivityPage />
            </UsersAccessGate>
          }
        />
        <Route
          path="organization/company"
          element={
            <SettingsAccessGate>
              <OrganizationCompanyPage />
            </SettingsAccessGate>
          }
        />
        <Route path="account" element={<AccountPreferencesLayout />}>
          <Route index element={<Navigate to="theme" replace />} />
          <Route path="language" element={<LanguageSettingsPage />} />
          <Route path="theme" element={<ThemeSettingsPage />} />
        </Route>
        <Route path="settings" element={<LegacySettingsIndexRedirect />} />
        <Route path="settings/organization" element={<LegacySettingsOrganizationRedirect />} />
        <Route path="settings/language" element={<LegacySettingsLanguageRedirect />} />
        <Route path="settings/theme" element={<LegacySettingsThemeRedirect />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  )
}
